import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Switch,
    FormControlLabel,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Button,
} from '@mui/material';
import Header from './Header';
import ConfirmDialog from './ConfirmDialog';
import api from '../api/api';

function ManageUsersPage() {
    const { t } = useTranslation();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [openUserDialog, setOpenUserDialog] = useState(false);
    const [currentUserData, setCurrentUserData] = useState(null);
    const [isEditModeUser, setIsEditModeUser] = useState(false);

    const [selectedUser, setSelectedUser] = useState(null);
    const [openSmartDeviceDialog, setOpenSmartDeviceDialog] = useState(false);
    const [allSmartDevices, setAllSmartDevices] = useState([]);
    const [userSmartDevicesState, setUserSmartDevicesState] = useState([]);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    useEffect(() => {
        fetchUsers();
        fetchAllSmartDevices();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/users');
            setUsers(response.data);
            setError('');
        } catch (err) {
            setError(t('common.errorOccurred', { message: err.response?.data?.message || err.message }));
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllSmartDevices = async () => {
        try {
            const response = await api.get('/smart-device');
            setAllSmartDevices(response.data);
        } catch (err) {
            console.error('Failed to fetch all smart devices:', err);
        }
    };

    const handleAddUser = () => {
        setIsEditModeUser(false);
        // Initialize 'locked' as false for new users
        setCurrentUserData({ username: '', password: '', name: '', email: '', role: 'ROLE_USER', locked: false });
        setOpenUserDialog(true);
    };

    const handleEditUser = (userToEdit) => {
        setIsEditModeUser(true);
        setCurrentUserData({ ...userToEdit, password: '' });
        setOpenUserDialog(true);
    };

    const handleUserDialogChange = (e) => {
        const { name, value, checked, type } = e.target;
        setCurrentUserData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSaveUser = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!currentUserData.username || (!isEditModeUser && !currentUserData.password) || !currentUserData.name) {
            setError(t('common.fillRequiredFields'));
            return;
        }

        try {
            if (isEditModeUser) {
                const updateData = {
                    name: currentUserData.name,
                    email: currentUserData.email,
                    role: currentUserData.role,
                    locked: currentUserData.locked,
                    id: currentUserData.id,
                    username: currentUserData.username,
                };

                if (currentUserData.password) {
                    updateData.password = currentUserData.password;
                }

                await api.post(`/user`, updateData);
                setMessage(t('common.userUpdatedSuccess'));
            } else {
                await api.post('/user', currentUserData);
                setMessage(t('common.userAddedSuccess'));
            }
            setOpenUserDialog(false);
            fetchUsers();
        } catch (err) {
            setError(t('common.errorOccurred', { message: err.response?.data?.message || err.message }));
        }
    };

    const handleCloseUserDialog = () => {
        setOpenUserDialog(false);
        setCurrentUserData(null);
    };

    const handleDeleteUser = (usernameToDelete) => {
        setUserToDelete(usernameToDelete);
        setDeleteConfirmOpen(true);
    };

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;
        try {
            await api.delete(`/user/${userToDelete}`);
            setMessage(t('common.userDeletedSuccess'));
            fetchUsers();
        } catch (err) {
            setError(t('common.errorOccurred', { message: err.response?.data?.message || err.message }));
        }
        setUserToDelete(null);
    };

    const handleManageSmartDevices = (user) => {
        setSelectedUser(user);
        const userDeviceCodes = new Set(user.smartDevices?.map(device => device.code) || []);
        const mappedSmartDevices = allSmartDevices.map(device => ({
            ...device,
            enabled: userDeviceCodes.has(device.code)
        }));
        setUserSmartDevicesState(mappedSmartDevices);
        setOpenSmartDeviceDialog(true);
    };

    const handleSmartDeviceToggle = (code) => {
        setUserSmartDevicesState((prevDevices) =>
            prevDevices.map((device) =>
                device.code === code ? { ...device, enabled: !device.enabled } : device
            )
        );
    };

    const handleSaveSmartDevices = async () => {
        if (!selectedUser) return;

        const devicesToAssign = userSmartDevicesState.map(device => ({
            code: device.code,
            enabled: device.enabled,
        }));

        try {
            await api.post(`/user/${selectedUser.username}/enable-smart-devices`, devicesToAssign);
            setMessage(t('common.smartDeviceSettingsUpdated'));
            setOpenSmartDeviceDialog(false);
            fetchUsers();
        } catch (err) {
            setError(t('common.errorOccurred', { message: err.response?.data?.message || err.message }));
        }
    };

    const handleCloseSmartDeviceDialog = () => {
        setOpenSmartDeviceDialog(false);
        setSelectedUser(null);
        setUserSmartDevicesState([]);
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Header title={t('common.manageUsers')} />

            <Container sx={{ mt: 4 }}>
                {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Button variant="contained" sx={{ mb: 2 }} onClick={handleAddUser}>
                    {t('common.addUser')}
                </Button>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="user table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('common.username')}</TableCell>
                                    <TableCell>{t('common.name')}</TableCell>
                                    <TableCell>{t('common.email')}</TableCell>
                                    <TableCell>{t('common.role')}</TableCell>
                                    <TableCell>{t('common.locked')}</TableCell> {/* New table header */}
                                    <TableCell align="right">{t('common.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((userRow) => (
                                    <TableRow
                                        key={userRow.username}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {userRow.username}
                                        </TableCell>
                                        <TableCell>{userRow.name}</TableCell>
                                        <TableCell>{userRow.email}</TableCell>
                                        <TableCell>{userRow.role === 'ROLE_ADMIN' ? 'Admin' : 'User'}</TableCell>
                                        <TableCell>{userRow.locked ? t('common.yes') : t('common.no')}</TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => handleEditUser(userRow)}>
                                                {t('common.edit')}
                                            </Button>
                                            <Button size="small" onClick={() => handleDeleteUser(userRow.username)} color="error">
                                                {t('common.delete')}
                                            </Button>
                                            <Button size="small" onClick={() => handleManageSmartDevices(userRow)}>
                                                {t('common.manageSmartDevices')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Container>

            <Dialog open={openUserDialog} onClose={handleCloseUserDialog}>
                <DialogTitle>{isEditModeUser ? t('common.editUser') : t('common.addUser')}</DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSaveUser} sx={{ mt: 1 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="username"
                            name="username"
                            label={t('common.username')}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={currentUserData?.username || ''}
                            onChange={handleUserDialogChange}
                            disabled={isEditModeUser}
                            required
                        />
                        <TextField
                            margin="dense"
                            id="password"
                            name="password"
                            label={t('common.password')}
                            type="password"
                            fullWidth
                            variant="standard"
                            value={currentUserData?.password || ''}
                            onChange={handleUserDialogChange}
                            required={!isEditModeUser}
                            helperText={isEditModeUser ? t('common.leaveBlankToKeepPassword') : ''}
                        />
                        <TextField
                            margin="dense"
                            id="name"
                            name="name"
                            label={t('common.name')}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={currentUserData?.name || ''}
                            onChange={handleUserDialogChange}
                            required
                        />
                        <TextField
                            margin="dense"
                            id="email"
                            name="email"
                            label={t('common.email')}
                            type="email"
                            fullWidth
                            variant="standard"
                            value={currentUserData?.email || ''}
                            onChange={handleUserDialogChange}
                            required={false}
                        />
                        <FormControl fullWidth margin="dense" variant="standard">
                            <InputLabel id="role-select-label">{t('common.role')}</InputLabel>
                            <Select
                                labelId="role-select-label"
                                id="role"
                                name="role"
                                value={currentUserData?.role || 'ROLE_USER'}
                                label={t('common.role')}
                                onChange={handleUserDialogChange}
                            >
                                <MenuItem value="ROLE_USER">User</MenuItem>
                                <MenuItem value="ROLE_ADMIN">Admin</MenuItem>
                            </Select>
                        </FormControl>
                        {/* New Locked Switch */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={currentUserData?.locked || false}
                                    onChange={handleUserDialogChange}
                                    name="locked"
                                    color="primary"
                                />
                            }
                            label={t('common.locked')}
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseUserDialog}>{t('common.cancel')}</Button>
                    <Button onClick={handleSaveUser} variant="contained">{t('common.save')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openSmartDeviceDialog} onClose={handleCloseSmartDeviceDialog} fullWidth maxWidth="md">
                <DialogTitle>{t('common.manageSmartDevices')} for {selectedUser?.username}</DialogTitle>
                <DialogContent>
                    {allSmartDevices.length === 0 ? (
                        <DialogContentText>
                            {t('common.noSmartDevicesAvailable')}
                        </DialogContentText>
                    ) : (
                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('common.deviceCode')}</TableCell>
                                        <TableCell>{t('common.description')}</TableCell>
                                        <TableCell>{t('common.type')}</TableCell>
                                        <TableCell>{t('common.status')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {userSmartDevicesState.map((device) => (
                                        <TableRow key={device.code}>
                                            <TableCell>{device.code}</TableCell>
                                            <TableCell>{device.description}</TableCell>
                                            <TableCell>{device.type}</TableCell>
                                            <TableCell>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={device.enabled}
                                                            onChange={() => handleSmartDeviceToggle(device.code)}
                                                            name="enabledStatus"
                                                            color="primary"
                                                        />
                                                    }
                                                    label={device.enabled ? t('common.enabled') : t('common.disabled')}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSmartDeviceDialog}>{t('common.cancel')}</Button>
                    <Button onClick={handleSaveSmartDevices} variant="contained">{t('common.save')}</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={confirmDeleteUser}
                title={t('common.confirmDelete')}
                message={t('common.confirmDeleteUser', { username: userToDelete })}
            />
        </Box>
    );
}

export default ManageUsersPage;